import { FC } from "react";
import "./ChatMessageList.scss";
import IAThinking from "../IAThinking/IAThinking";
import { ChatMessageModel } from "../../../models/Chat/ChatMessageModel";
import ChatMessage from "../ChatMessage/ChatMessage";

interface ChatMessageListProps {
  messages?: ChatMessageModel[] | null;
  isStreaming?: boolean;
  /** AI-940: geração sendo interrompida -> loader com mensagens de cancelamento. */
  isCancelling?: boolean;
}

// AI-940: mensagens do loader enquanto a interrupção é processada.
const CANCELLING_MESSAGES = ["Cancelando...", "Interrompendo a resposta..."];

// Uma resposta enviada PELO formulário sempre chega no formato markdown
// "**pergunta** resposta" (ver ChatQuiz.submitAll). Uma mensagem LIVRE digitada na
// barra (mudança de assunto durante o refinamento) não tem esse formato — e não
// deve entrar no box do formulário, senão aparece como se fosse uma resposta dele
// (AI-947).
const isQuizAnswerSummary = (content?: string | null): boolean =>
  /^\s*\*\*/.test(content ?? "");

const ChatMessageList: FC<ChatMessageListProps> = ({
  messages,
  isStreaming,
  isCancelling,
}) => {
  const renderMessages = () => {
    if (!messages) return null;

    // Mensagem de usuário que responde a um formulário (quiz): será escondida,
    // pois o resumo das respostas aparece no lugar do próprio formulário. Só vale
    // para respostas vindas do próprio formulário (resumo "**pergunta** resposta").
    const skip = new Set<number>();
    messages.forEach((m, i) => {
      if (
        m?.sender !== "user" &&
        m?.quiz &&
        messages[i + 1]?.sender === "user" &&
        isQuizAnswerSummary(messages[i + 1]?.content)
      ) {
        skip.add(i + 1);
      }
    });

    return messages.map((message: ChatMessageModel, index: number) => {
      if (skip.has(index)) return null;

      const isUser = message?.sender === "user";
      const articles = Array.isArray(message?.articles) ? message.articles : [];

      // Se este é o formulário e a próxima mensagem é a resposta do usuário VINDA
      // do formulário (resumo), renderiza o resumo respondido no lugar do form.
      const quizAnswers =
        !isUser &&
        message?.quiz &&
        messages[index + 1]?.sender === "user" &&
        isQuizAnswerSummary(messages[index + 1]?.content)
          ? messages[index + 1]?.content
          : undefined;

      // Resposta do agente logo após o envio do formulário (form -> resumo -> aqui):
      // esconde o título de categoria para parecer uma resposta única. Só quando a
      // mensagem do meio é de fato o resumo do formulário, não uma pergunta nova.
      const hideTitle =
        !isUser &&
        messages[index - 1]?.sender === "user" &&
        !!messages[index - 2]?.quiz &&
        isQuizAnswerSummary(messages[index - 1]?.content);

      return (
        <ChatMessage
          key={message?.id ?? index}
          message={message}
          isUser={isUser}
          articles={articles}
          isLast={index === messages.length - 1}
          quizAnswers={quizAnswers ?? undefined}
          hideTitle={hideTitle}
        />
      );
    });
  };

  return (
    <div className="ChatMessageList" data-testid="ChatMessageList">
      {renderMessages()}

      {(isStreaming || isCancelling) && (
        <div className="chat-message agent-message">
          <div className="message-content">
            <IAThinking
              resetKey={messages?.length ?? 0}
              messages={isCancelling ? CANCELLING_MESSAGES : undefined}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatMessageList;
